import { useState, useMemo } from 'react';
import { TimelinePhaseColumn } from './TimelinePhaseColumn';
import { useBrand } from '@/config/brands';
import type { LocalScenarioProcedure } from '@/lib/scenarioStorage';

interface ScenarioTimelineProps {
  procedures: LocalScenarioProcedure[];
  phases: string[];
  onRemoveProcedure: (index: number) => void;
  onEditProcedure: (index: number) => void;
  onUpdateProcedurePhase: (index: number, newPhase: string) => void;
}

export function ScenarioTimeline({
  procedures,
  phases,
  onRemoveProcedure,
  onEditProcedure,
  onUpdateProcedurePhase,
}: ScenarioTimelineProps) {
  const brand = useBrand();
  const isAhp = brand.key === 'ahp';
  const [dragPhase, setDragPhase] = useState<string | null>(null);
  const [dragIndex, setDragIndex] = useState<number | null>(null);

  // Group procedures by phase, preserving original index
  const groupedByPhase = useMemo(() => {
    const groups = new Map<string, (LocalScenarioProcedure & { originalIndex: number })[]>();

    // Initialize all phases
    for (const phase of phases) {
      groups.set(phase, []);
    }

    // Assign procedures to their phases
    procedures.forEach((proc, idx) => {
      const phase = proc.phase || phases[0] || 'Treatment';
      const group = groups.get(phase);
      if (group) {
        group.push({ ...proc, originalIndex: idx });
      } else {
        // Phase not in list — create it
        groups.set(phase, [{ ...proc, originalIndex: idx }]);
      }
    });

    return groups;
  }, [procedures, phases]);

  const handleDragStart = (e: React.DragEvent, index: number) => {
    setDragIndex(index);
    e.dataTransfer.effectAllowed = 'move';
    e.dataTransfer.setData('text/plain', String(index));
  };

  const handleDragOver = (e: React.DragEvent) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = 'move';
  };

  const handleDrop = (e: React.DragEvent, targetPhase: string) => {
    e.preventDefault();
    setDragPhase(null);

    const sourceIndex = parseInt(e.dataTransfer.getData('text/plain'));
    if (!isNaN(sourceIndex) && procedures[sourceIndex]?.phase !== targetPhase) {
      onUpdateProcedurePhase(sourceIndex, targetPhase);
    }
    setDragIndex(null);
  };

  const handleDragEnter = (phase: string) => {
    setDragPhase(phase);
  };

  if (procedures.length === 0) {
    return (
      <div className={`text-center py-10 border-2 border-dashed rounded-xl ${
        isAhp ? 'border-gray-200' : 'border-white/10'
      }`}>
        <p className="text-sm text-muted-foreground">No procedures yet</p>
        <p className="text-[10px] text-muted-foreground mt-1">Search for codes above or pick a template to get started</p>
      </div>
    );
  }

  return (
    <div className="space-y-2">
      <div className="flex items-center justify-between">
        <h3 className={`text-xs font-bold uppercase tracking-wide ${
          isAhp ? 'text-[#2b5e49]' : 'text-matrix-green'
        }`}>
          Treatment Timeline
        </h3>
        <span className="text-[10px] text-muted-foreground">
          Drag procedures between phases
        </span>
      </div>

      {/* Phase columns — horizontal scroll on desktop, vertical stack on mobile */}
      <div className="flex gap-3 overflow-x-auto pb-2 thin-scrollbar sm:flex-row flex-col">
        {Array.from(groupedByPhase.entries()).map(([phase, procs]) => (
          <div
            key={phase}
            onDragEnter={() => handleDragEnter(phase)}
            className="sm:flex-1 sm:min-w-[200px]"
          >
            <TimelinePhaseColumn
              phase={phase}
              procedures={procs}
              onRemoveProcedure={onRemoveProcedure}
              onEditProcedure={onEditProcedure}
              onDragStart={handleDragStart}
              onDragOver={handleDragOver}
              onDrop={handleDrop}
              isDropTarget={dragPhase === phase && dragIndex !== null}
            />
          </div>
        ))}
      </div>
    </div>
  );
}
